import { useEffect, useRef } from "react";
import { useQuery, type QueryClient } from "@tanstack/react-query";
import { fetchAccounts, type AccountRow } from "@/lib/reportQueries";

/** Every account of the company (all statuses), full report columns. */
export type CompanyAccountName = AccountRow;

/** Fetches every account of the company in 1,000-row pages (never capped). */
export async function fetchCompanyAccountNames(companyId: string): Promise<CompanyAccountName[]> {
  return fetchAccounts(companyId);
}

export const companyAccountsKey = (companyId?: string) => ["company-accounts", companyId] as const;
const ACCOUNTS_STALE = 5 * 60_000;

/** Shared, cached account list keyed by company. */
export function useCompanyAccounts(companyId?: string, enabled = true) {
  return useQuery({
    queryKey: companyAccountsKey(companyId),
    enabled: enabled && !!companyId,
    staleTime: ACCOUNTS_STALE,
    queryFn: () => fetchCompanyAccountNames(companyId!),
  });
}

/** Cached list for imperative loaders (reports, entry view). */
export function ensureCompanyAccounts(qc: QueryClient, companyId: string) {
  return qc.ensureQueryData({
    queryKey: companyAccountsKey(companyId),
    staleTime: ACCOUNTS_STALE,
    queryFn: () => fetchCompanyAccountNames(companyId),
  });
}

/** Fresh list: used once when a line references an account missing from the cache. */
export function refetchCompanyAccounts(qc: QueryClient, companyId: string) {
  return qc.fetchQuery({
    queryKey: companyAccountsKey(companyId),
    staleTime: 0,
    queryFn: () => fetchCompanyAccountNames(companyId),
  });
}

/** Cached list, refetched once if any of `ids` is unknown to it. */
export async function companyAccountsCovering(qc: QueryClient, companyId: string, ids: Iterable<string>) {
  let list = await ensureCompanyAccounts(qc, companyId);
  const known = new Set(list.map((a) => a.id));
  for (const id of ids) {
    if (id && !known.has(id)) { list = await refetchCompanyAccounts(qc, companyId); break; }
  }
  return list;
}

/**
 * Hook guard: when displayed lines reference an account the cached list does
 * not know (created elsewhere), refetch the list once so no line is nameless.
 */
export function useRefetchOnMissingAccounts(
  query: { data?: CompanyAccountName[]; isFetching: boolean; refetch: () => unknown },
  accountIds: string[],
) {
  const tried = useRef<string>("");
  useEffect(() => {
    if (!query.data || query.isFetching || accountIds.length === 0) return;
    const known = new Set(query.data.map((a) => a.id));
    const missing = accountIds.filter((id) => id && !known.has(id)).sort().join(",");
    if (!missing || tried.current === missing) return;
    tried.current = missing;
    void query.refetch();
  }, [query, accountIds]);
}

/** App-lifetime: any chart-of-accounts write clears the shared list at once. */
export function subscribeCompanyAccountsInvalidation(qc: QueryClient) {
  const on = (e: Event) => {
    const companyId = (e as CustomEvent).detail?.companyId as string | undefined;
    void qc.invalidateQueries({ queryKey: companyId ? companyAccountsKey(companyId) : ["company-accounts"] });
  };
  window.addEventListener("sida:accounts-changed", on);
  return () => window.removeEventListener("sida:accounts-changed", on);
}
